import React, { useEffect, useState } from 'react';
import {
  LayoutDashboard, Lightbulb, ClipboardList, Video, Film, MessageCircle,
  CheckCircle2, Send, TrendingUp, AlertTriangle, Users, PartyPopper,
} from 'lucide-react';
import { mccGet } from './mccApi';
import { toast } from 'sonner';
import { StatCard, SectionHeader, PlatformIcon, Avatar, AvatarStack, DueBadge, EmptyState, StatusPill } from './mccUi';
import { PLATFORMS, statusTone, humanDate, platformMeta } from './mccConstants';
import MccContentCard from './MccContentCard';

export default function MccDashboard({ team, onOpenProject, onNav }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nameOf = (uid) => (team.find((t) => t.id === uid) || {}).name || uid;

  const load = async () => {
    setLoading(true);
    try {
      const d = await mccGet('/marketing/dashboard');
      setData(d);
    } catch (e) { toast.error(e.message || 'Failed to load dashboard'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return (
      <div className="p-6 space-y-4">
        <div className="animate-pulse space-y-3">
          <div className="h-8 w-64 bg-slate-200 rounded" />
          <div className="grid grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-24 bg-slate-100 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  const workloadEntries = Object.entries(data.workload || {})
    .map(([uid, count]) => ({ uid, count, name: nameOf(uid) }))
    .sort((a, b) => b.count - a.count).slice(0, 8);

  return (
    <div className="p-6 space-y-6">
      <SectionHeader
        title="Marketing & Media Dashboard"
        subtitle="Real-time view of ideas, production, approvals, publishing and workload across every platform."
        icon={LayoutDashboard}
      />

      {/* Top stat grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Assigned to me" value={data.assigned_to_me_count}
          sublabel={`${data.assigned_to_me_breakdown.projects} projects · ${data.assigned_to_me_breakdown.requests} requests`}
          tone="indigo" icon={Users}
          onClick={() => onNav('my-work')} testid="mcc-dash-assigned-me" />
        <StatCard label="New requests" value={data.new_requests_count} tone="sky" icon={ClipboardList}
          onClick={() => onNav('requests')} testid="mcc-dash-new-requests" />
        <StatCard label="Awaiting approval" value={data.awaiting_approval} tone="amber" icon={CheckCircle2}
          onClick={() => onNav('approvals')} testid="mcc-dash-awaiting" />
        <StatCard label="Changes required" value={data.changes_required} tone="rose" icon={AlertTriangle}
          onClick={() => onNav('reviews')} testid="mcc-dash-changes" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Ideas to review" value={data.new_ideas_count} tone="violet" icon={Lightbulb}
          onClick={() => onNav('ideas')} testid="mcc-dash-ideas" />
        <StatCard label="Ready to publish" value={data.ready_to_publish} tone="emerald" icon={Send}
          onClick={() => onNav('publishing')} testid="mcc-dash-ready" />
        <StatCard
          label="Upcoming filming (7d)" value={(data.upcoming_filming || []).length}
          tone="slate" icon={Video} onClick={() => onNav('filming-calendar')}
        />
        <StatCard label="Recently completed" value={(data.recently_completed || []).length}
          tone="emerald" icon={PartyPopper} onClick={() => onNav('completed')} />
      </div>

      {/* Overdue + Upcoming Filming */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Overdue projects" tone="rose" onSeeAll={() => onNav('platforms')}>
          {data.overdue_projects.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-6">Nothing overdue 🎉</p>
          ) : (
            <div className="grid gap-2">
              {data.overdue_projects.slice(0, 5).map((p) => (
                <MccContentCard key={p.id} item={p} nameOf={nameOf} onClick={() => onOpenProject(p.id)} />
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Upcoming filming" tone="violet" onSeeAll={() => onNav('filming-calendar')}>
          {data.upcoming_filming.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-6">No filming scheduled in the next 7 days.</p>
          ) : data.upcoming_filming.slice(0, 5).map((f) => (
            <div key={f.id} className="border border-slate-200 rounded-lg px-3 py-2 mb-2 hover:bg-slate-50">
              <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-1">
                <span className="font-bold text-violet-700">{humanDate(f.filming_date)}</span>
                {f.filming_time ? <span>· {f.filming_time}</span> : null}
                <StatusPill status={f.status} size="sm" />
              </div>
              <p className="text-sm font-semibold text-slate-900">{f.title}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {f.person_being_filmed ? `👤 ${f.person_being_filmed}` : ''}{f.location ? ` · 📍 ${f.location}` : ''}
              </p>
            </div>
          ))}
        </Panel>
      </div>

      {/* Platform activity + Workload */}
      <div className="grid lg:grid-cols-[1fr_320px] gap-4">
        <Panel title="Platform activity" tone="indigo">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {PLATFORMS.map((p) => {
              const c = (data.platform_counts || {})[p.v] || { active: 0, published: 0 };
              return (
                <button key={p.v} onClick={() => { onNav('platforms'); localStorage.setItem('mcc-last-platform', p.v); }}
                  className="text-left rounded-lg border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/30 p-3 transition"
                  data-testid={`mcc-platform-tile-${p.v}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <PlatformIcon platform={p.v} size={24} />
                    <span className="text-sm font-semibold text-slate-800">{p.l}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">{c.active} active</span>
                    <span className="font-semibold text-emerald-700">{c.published} published</span>
                  </div>
                </button>
              );
            })}
          </div>
        </Panel>

        <Panel title="Team workload" tone="slate" onSeeAll={() => onNav('team-view')}>
          {workloadEntries.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-6">No active projects yet.</p>
          ) : (
            <div className="space-y-2">
              {workloadEntries.map(({ uid, count, name }) => (
                <div key={uid} className="flex items-center gap-2">
                  <Avatar name={name} size={22} />
                  <span className="text-sm text-slate-800 flex-1 truncate">{name}</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{count}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* Recently completed */}
      <Panel title="Recently completed" tone="emerald" onSeeAll={() => onNav('completed')}>
        {data.recently_completed.length === 0 ? (
          <p className="text-xs text-slate-500 text-center py-6">No completed content yet — go make some magic.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.recently_completed.slice(0, 6).map((p) => (
              <MccContentCard key={p.id} item={p} nameOf={nameOf} onClick={() => onOpenProject(p.id)} />
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function Panel({ title, children, onSeeAll, tone = 'indigo' }) {
  const toneAccent = {
    rose: '#e11d48', indigo: '#4f46e5', violet: '#7c3aed', emerald: '#059669', slate: '#475569',
  }[tone] || '#4f46e5';
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-2.5 flex items-center justify-between border-b border-slate-100">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-4 rounded-sm" style={{ background: toneAccent }} />
          <p className="text-sm font-semibold text-slate-900">{title}</p>
        </div>
        {onSeeAll ? <button onClick={onSeeAll} className="text-[11px] text-indigo-600 hover:underline font-medium">See all →</button> : null}
      </div>
      <div className="p-3">
        {children}
      </div>
    </div>
  );
}
